import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, ZoomIn, ZoomOut, Maximize2, Printer, FileDown, FileSpreadsheet, Plus, FileText, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { orgchartApi, vacancyApi, masterApi, employeeApi, resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import { departmentTree, departmentGroups, inDepartmentGroup, displayDepartment } from '../lib/utils';
import { captureChart, chartCanvasToPdf, DOC_CONTROL, SIGN_OFF } from '../lib/chartPdf';

const CARD_W = 200, CARD_H = 96, GAP_X = 44, ROW_H = 168;
// The column down the left that names each row's grade — L12 at the top, L5 at
// the bottom. The chart is drawn one grade per row, so the ladder it stands on
// should be readable off it rather than inferred from the pill on each card.
const RAIL_W = 62;


// Styles for the printable sheet. They live with the sheet in a portal that is
// only mounted while a print is in flight, so `body > *:not(...)` can hide the
// app shell without ever affecting any other page's printing.
const PRINT_SHEET_CSS = `
[data-org-print-sheet] { display: none; }
@media print {
  @page { size: A4 landscape; margin: 10mm; }
  html, body { background: #fff !important; }
  body > *:not([data-org-print-sheet]) { display: none !important; }
  [data-org-print-sheet] { display: block !important; }
  [data-org-print-sheet] * { box-sizing: border-box; }
  .org-sheet { display: flex; flex-direction: column; height: 100vh;
               font-family: Arial, Helvetica, sans-serif; color: #11182B; }
  .org-sheet-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  .org-sheet-head h1 { font-size: 17px; margin: 0 0 3px; font-weight: 700; }
  .org-sheet-sub { font-size: 10px; color: #5A6377; }
  /* Fixed width, pinned right: the box lands in the same corner at the same
     size on every sheet, whatever proportions the chart underneath has. */
  .org-doc-box { border-collapse: collapse; border: 1px solid #11182B; width: 210px; flex: none; }
  .org-doc-box th, .org-doc-box td { font-size: 9.5px; padding: 3px 6px; text-align: left; white-space: nowrap; }
  .org-doc-box th { font-weight: 700; width: 74px; }
  .org-doc-box td { font-weight: 400; }
  .org-doc-box tr + tr th, .org-doc-box tr + tr td { border-top: 1px solid #11182B; }
  .org-sheet-chart { flex: 1 1 auto; min-height: 0; display: flex;
                     align-items: center; justify-content: center; padding: 10px 0; }
  .org-sheet-chart img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .org-sheet-foot { display: flex; justify-content: space-between; gap: 24px; flex: none;
                    border-top: 1px solid #D6DBE6; padding-top: 7px; font-size: 11px; }
  .org-sheet-foot b { font-weight: 700; }
}
`;

const LEVEL_COLORS = {
  1:  { bg: '#F4F1FA', border: '#D8CFEC', text: '#5A3FA0' },
  2:  { bg: '#FDF2F8', border: '#F5C7DD', text: '#A23A6E' },
  3:  { bg: '#FEF6E7', border: '#F5D78A', text: '#8A5A12' },
  4:  { bg: '#FEF1E7', border: '#F5BE96', text: '#A35418' },
  5:  { bg: '#FEEAEA', border: '#F4B5B5', text: '#A23030' },
  6:  { bg: '#F0F5FE', border: '#BFD2F4', text: '#1F4FAD' },
  7:  { bg: '#EAF4FE', border: '#A8CCF1', text: '#15549E' },
  8:  { bg: '#E8F6FB', border: '#A6D4E6', text: '#0E6B8C' },
  9:  { bg: '#EAF6F1', border: '#A8D8C0', text: '#137A52' },
  10: { bg: '#E7F7EF', border: '#9CD6B7', text: '#0F7A52' },
  11: { bg: '#F4F6F9', border: '#D1D7E2', text: '#3A4760' },
  // The ladder runs to L15. Without these, every grade above 11 was painted
  // with L11's colours and four distinct grades read as one band.
  12: { bg: '#F1F4FA', border: '#C9D3E6', text: '#2F3E5C' },
  13: { bg: '#EEF1F8', border: '#C0CCE2', text: '#293754' },
  14: { bg: '#EAEEF7', border: '#B6C4DD', text: '#22304B' },
  15: { bg: '#E6EBF5', border: '#AEBDD9', text: '#1B2842' },
};
const levelColor = (lv) => LEVEL_COLORS[lv] || LEVEL_COLORS[11];

// "L1–L4", "L1, L3" — however the hidden set actually reads, so the note on the
// chart stays true if somebody reconfigures it.
const levelRanges = (levels) => {
  const sorted = [...new Set(levels || [])].sort((a, b) => a - b);
  const parts = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    parts.push(i === j ? `L${sorted[i]}` : `L${sorted[i]}–L${sorted[j]}`);
    i = j + 1;
  }
  return parts.join(', ');
};

// The three converted top-leadership nodes keep their original seeded ids.
// Live db converted Sanjeev/Anmol into real employees with new uuids — include those too.
const TOP_LEADER_IDS = [
  'LEAD_MD', 'LEAD_ED', 'LEAD_SANBER',
  'b586e934-0d19-42cb-8747-1aa2dbb4a9a2', // Sanjeev Gumber (MD)
  'a34df2db-57f6-4f38-a697-c38b93a4a92b', // Anmol Gumber (ED)
];

export default function OrgChart() {
  const { activeUnit, isAllUnits } = useUnit();
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [searchParams] = useSearchParams();
  const [deptFilter, setDeptFilter] = useState(searchParams.get('department') || '');
  useEffect(() => { setDeptFilter(searchParams.get('department') || ''); }, [searchParams]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(0.8);
  const contentRef = useRef(null);
  const viewRef = useRef(null);
  // Printable sheet: null except while a print is being prepared / is on screen.
  const [printSheet, setPrintSheet] = useState(null);
  const [printing, setPrinting] = useState(false);
  const printImgRef = useRef(null);

  const canManage = hasPerm('employee.create');
  // OPENING a position is now its own permission, admin-held. It used to ride
  // on `employee.create`, so anyone allowed to add a person could also
  // declare open headcount — two different decisions behind one key. Editing
  // an existing vacancy is unchanged; only creation is restricted.
  const canCreateVacancy = hasPerm('vacancy.create');

  const [editNode, setEditNode] = useState(null);   // { id, name, unit, level, designation, department, reporting_to }
  const [editMgrs, setEditMgrs] = useState([]);

  const openEditNode = async (n) => {
    setEditNode({
      id: n.id, name: n.name, unit: n.unit, level: n.level,
      designation: n.designation || '', department: n.department || '', reporting_to: n.reporting_to || '',
      _origDesignation: n.designation || '', _origUnit: n.unit || '', _origDepartment: n.department || '',
    });
    try {
      const r = await employeeApi.managers({ unit: n.unit, for_level: n.level });
      setEditMgrs(r.data.filter((m) => m.id !== n.id));
    } catch { setEditMgrs([]); }
  };

  const saveNode = async () => {
    if (!editNode) return;
    try {
      await employeeApi.update(editNode.id, {
        designation: editNode.designation,
        department: editNode.department,
        reporting_to: editNode.reporting_to || null,
      });
      // If this was a promotion or a transfer, hand off to the profile's letter flow.
      const lvl = (name) => { const d = (masters.designations || []).find((x) => x.name === name); return d && d.level != null ? d.level : null; };
      const oldLv = lvl(editNode._origDesignation), newLv = lvl(editNode.designation);
      const isPromotion = editNode.designation !== editNode._origDesignation && (oldLv == null || newLv == null ? true : newLv > oldLv);
      const unitChanged = editNode.department !== undefined && (editNode.unit !== editNode._origUnit);
      const deptChanged = editNode.department !== editNode._origDepartment;
      const id = editNode.id;
      const params = new URLSearchParams();
      if (isPromotion) { params.set('letter', 'promotion'); params.set('from', editNode._origDesignation); params.set('to', editNode.designation); }
      else if (unitChanged || deptChanged) { params.set('letter', 'transfer'); params.set('fu', editNode._origUnit); params.set('tu', editNode.unit); params.set('fd', editNode._origDepartment); params.set('td', editNode.department); }
      toast.success('Updated — reflected across the app');
      setEditNode(null);
      if (params.toString()) { navigate(`/employees/${id}?${params.toString()}`); return; }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  // dialogs
  const [vacForm, setVacForm] = useState(null);   // {id?, title, department, unit, reporting_to, notes, idem}
  // Guard against double-clicks / long JD generation. Also lets us render
  // "Saving…" so users see something is happening.
  const [savingVac, setSavingVac] = useState(false);
  const [jdVac, setJdVac] = useState(null);        // vacancy node the JD dialog is open for
  const [jdText, setJdText] = useState('');
  const [jdMethod, setJdMethod] = useState('');
  const [jdLoading, setJdLoading] = useState(false);
  const [masters, setMasters] = useState({ departments: [], designations: [], units: [] });
  useEffect(() => {
    Promise.all([masterApi.departments(), masterApi.designations(), masterApi.units()])
      .then(([d, g, u]) => setMasters({ departments: d.data, designations: g.data, units: u.data }))
      .catch(() => {});
  }, []);
  const [convertFor, setConvertFor] = useState(null);
  const [convUnit, setConvUnit] = useState('Corporate Office');
  const [convCorp, setConvCorp] = useState(true);
  const [convBusy, setConvBusy] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeUnit]);
  const openConvert = (n) => {
    setConvertFor(n);
    setConvUnit(n.unit || 'Corporate Office');
    setConvCorp(true);
  };
  const doConvert = async () => {
    if (!convertFor) return;
    if (!convUnit) { toast.error('Pick a unit to assign'); return; }
    setConvBusy(true);
    try {
      await employeeApi.convertToEmployee(convertFor.id, { unit: convUnit, is_corporate: convCorp });
      toast.success(`${convertFor.name} is now an employee`);
      setConvertFor(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not convert'); }
    finally { setConvBusy(false); }
  };
  const load = async () => {
    setLoading(true);
    // The server returns only the grades configured as visible on the chart —
    // L5–L15 by default. L1–L4 stay in the HRMS and in every other module;
    // they are simply not drawn here. See /orgchart/level-visibility.
    try { const r = await orgchartApi.get({ unit: activeUnit }); setNodes(r.data); }
    catch (e) { toast.error('Failed to load org chart'); }
    finally { setLoading(false); }
  };

  // Which roles the Designations master puts on a rung — the SAME master the
  // levels themselves come from, so the rail can name a vacant rung ("L12 —
  // Business Head — vacant") without a second list kept in the chart.
  const designationsAt = (level) => (masters.designations || [])
    .filter((d) => d.level === level).map((d) => d.name).join(', ');

  // What the chart is configured to draw, so the page can say what it leaves
  // out instead of looking as though those people are missing.
  const [levelView, setLevelView] = useState(null);
  useEffect(() => {
    orgchartApi.levelVisibility()
      .then((r) => setLevelView(r.data))
      .catch(() => { /* the note is informational — never block the chart */ });
  }, []);

  const employeeNodes = useMemo(() => nodes.filter((n) => !n.is_vacant && !n.org_only), [nodes]);

  const { positioned, edges, width, height, byId, levelRows } = useMemo(() => {
    let src = nodes;
    if (deptFilter) {
      // Department view: the department's people + their reporting chain up to the top,
      // and ALL org-only top management (MD/ED/etc.) — always shown at the top.
      const nById = Object.fromEntries(nodes.map((n) => [n.id, n]));
      const keep = new Set();
      // Picking a group (Quality) draws everything inside it — QA, QC, SQA,
      // Standard Room and the two superseded spellings — as one chart, plus
      // any quality-named department not yet grouped in the master.
      nodes.filter((n) => inDepartmentGroup(masters.departments, deptFilter, n.department)
                          && !n.is_vacant).forEach((m) => {
        keep.add(m.id);
        let cur = m, guard = 0;
        while (cur && cur.reporting_to && nById[cur.reporting_to] && guard < 30) {
          keep.add(cur.reporting_to); cur = nById[cur.reporting_to]; guard += 1;
        }
      });
      nodes.forEach((n) => { if (n.org_only) keep.add(n.id); });
      src = nodes.filter((n) => keep.has(n.id));
    }
    const byId = Object.fromEntries(src.map((n) => [n.id, n]));
    const children = {};
    src.forEach((n) => { const p = byId[n.reporting_to] ? n.reporting_to : null; (children[p] = children[p] || []).push(n); });
    // Senior first, then by name, so the chart draws the same way every time
    // and a head sits left of their team rather than wherever the employee list
    // happened to put them.
    const order = (a, b) => (b.level || 0) - (a.level || 0)
      || (a.is_vacant ? 1 : 0) - (b.is_vacant ? 1 : 0)
      || String(a.name || '').localeCompare(String(b.name || ''));
    Object.values(children).forEach((list) => list.sort(order));

    // Everyone who reports to somebody on this chart hangs off them; everyone
    // else is a root. The most senior is the real one — the rest are people
    // whose manager has not been recorded, drawn after the whole organisation
    // rather than appearing mid-chart with no line into them.
    const roots = (children[null] || []).slice().sort(order);

    // A ROW IS A GRADE. Everyone on L6 sits on the L6 line, whoever they report
    // to — so rank can be read straight off the chart instead of having to
    // check the pill on every card. Rows used to be depth in the reporting
    // line, which drew an L7 Assistant Manager level with the L6 Sr Executives
    // beside him purely because both reported to the same DGM.
    //
    // Every grade the ladder RUNS THROUGH gets a row, between the top and the
    // bottom of what this chart holds — including one nobody occupies, which
    // is drawn as a vacant rung rather than closed up. A rung that exists in
    // the structure and has nobody on it (L12 Business Head) is information;
    // silently removing the row says the ladder goes straight from L14 to L11.
    const lvlOf = (n) => (n.level == null ? 0 : n.level);
    const present = [...new Set(src.map(lvlOf))];
    const top = Math.max(...present, 0), bottom = Math.min(...present, 0);
    const visible = new Set(levelView?.visible || []);
    const ladder = [];
    for (let lv = top; lv >= bottom; lv -= 1) {
      if (present.includes(lv) || visible.has(lv)) ladder.push(lv);
    }
    const bandOf = {};
    ladder.forEach((lv, i) => { bandOf[lv] = i; });

    // The one case a grade cannot settle on its own: a manager and their report
    // on the SAME grade. They cannot share a row — the line between them would
    // run sideways — so the junior drops below. The junior must NOT drop onto
    // the next grade's row either: VIJAY (L8) under HAIDER ALI (L8) landed on
    // the L7 line next to PANKAJ (L7), and the chart then showed an L8 and an
    // L7 as equals, which is the whole thing rows are supposed to tell you.
    //
    // So a pushed node gets a row of its OWN. Each node is keyed by
    // (grade band, how many same-grade managers it sits under); every distinct
    // key becomes its own row, in order. One grade per row always holds,
    // because two different grades can never produce the same key.
    //
    // A report who OUTRANKS their manager keeps their grade's row, above the
    // manager, and the line is drawn going up (see `inverted` below). That is a
    // real anomaly in the reporting data and the chart shows it rather than
    // quietly re-ranking somebody to hide it.
    const bandIdx = {}, pushIdx = {};
    const xOrder = {}; let cursor = 0;
    const place = (id, parentId, seen) => {
      if (seen.has(id)) return;              // a reporting loop; stop here
      seen.add(id);
      const band = bandOf[lvlOf(byId[id] || {})] || 0;
      bandIdx[id] = band;
      pushIdx[id] = (parentId != null && bandIdx[parentId] === band)
        ? (pushIdx[parentId] || 0) + 1
        : 0;
      const kids = children[id] || [];
      kids.forEach((k) => place(k.id, id, seen));
      const placed = kids.map((k) => xOrder[k.id]).filter((v) => v != null);
      if (!placed.length) { xOrder[id] = cursor; cursor += 1; return; }
      // Centred over the span its people occupy, so a manager of nine sits in
      // the middle of them rather than above the first one.
      xOrder[id] = (Math.min(...placed) + Math.max(...placed)) / 2;
    };
    const seen = new Set();
    roots.forEach((r) => { place(r.id, null, seen); cursor += 0.5; });   // a gap between trees
    // Anything a cycle left unplaced is still drawn, rather than stacked at 0,0.
    src.forEach((n) => {
      if (bandIdx[n.id] == null) { bandIdx[n.id] = bandOf[lvlOf(n)] || 0; pushIdx[n.id] = 0; }
    });

    // Turn the keys into consecutive rows: grade order first, then the pushed
    // rows beneath their own grade.
    // Every band gets a row even when nobody stands on it — that is what keeps
    // a vacant rung on the chart instead of closing the gap over it.
    const rowOfKey = {};
    [...new Set([...src.map((n) => `${bandIdx[n.id]}:${pushIdx[n.id]}`),
                 ...ladder.map((lv) => `${bandOf[lv]}:0`)])]
      .map((k) => k.split(':').map(Number))
      .sort((a, b) => a[0] - b[0] || a[1] - b[1])
      .forEach(([b, p], i) => { rowOfKey[`${b}:${p}`] = i; });
    const depth = {};
    src.forEach((n) => { depth[n.id] = rowOfKey[`${bandIdx[n.id]}:${pushIdx[n.id]}`] || 0; });

    src.forEach((n) => {
      if (xOrder[n.id] == null) { xOrder[n.id] = cursor; cursor += 1; depth[n.id] = depth[n.id] || 0; }
    });

    const rootIds = new Set(roots.map((r) => r.id));
    const positioned = src.map((n) => ({
      ...n,
      // A root that is not the top of the organisation is somebody whose
      // manager has not been recorded. Flagged, rather than quietly drawn as
      // though they reported to nobody.
      noManager: rootIds.has(n.id) && roots.length > 1 && n.id !== roots[0].id,
      // Everything is shifted right by the level rail, so the grade labels have
      // a column of their own instead of sitting on top of the first card.
      x: RAIL_W + (xOrder[n.id] || 0) * (CARD_W + GAP_X),
      y: (depth[n.id] || 0) * ROW_H,
    }));

    // One heading per row. A ROW IS A GRADE (see above), so each row carries
    // the grade it holds — which is what makes the chart show the ladder it is
    // drawn on. A row with nobody on it is a rung the structure has and the
    // company has not filled: it says VACANT rather than disappearing.
    const headcount = {};
    positioned.forEach((n) => { headcount[n.y] = (headcount[n.y] || 0) + 1; });
    const levelRows = ladder.map((lv) => {
      const y = (rowOfKey[`${bandOf[lv]}:0`] || 0) * ROW_H;
      return { y, level: lv, count: headcount[y] || 0 };
    });
    // The pushed rows (a same-grade report dropped below their manager) carry
    // their grade's label too, so no row on the chart is left unlabelled.
    positioned.forEach((n) => {
      if (!levelRows.some((r) => r.y === n.y)) {
        levelRows.push({ y: n.y, level: n.level ?? null, count: headcount[n.y] || 0 });
      }
    });
    levelRows.sort((a, b) => a.y - b.y);
    const pById = Object.fromEntries(positioned.map((n) => [n.id, n]));
    // `inverted` marks a line that has to run UPWARDS because the report
    // outranks their own manager (a DGM reporting to a Manager). Flagged so it
    // leaves the right edge of the card and is drawn in a colour that says
    // "look at this", instead of being mistaken for a drawing fault.
    const edges = positioned.filter((n) => pById[n.reporting_to]).map((n) => ({
      from: pById[n.reporting_to], to: n, inverted: n.y < pById[n.reporting_to].y,
    }));
    const width = Math.max(...positioned.map((n) => n.x), 0) + CARD_W + GAP_X;
    const height = Math.max(0, ...positioned.map((n) => n.y)) + ROW_H + 40;
    return { positioned, edges, width, height, byId, levelRows };
  }, [nodes, deptFilter, masters.departments, levelView]);

  const fit = () => {
    const vw = viewRef.current?.clientWidth || 1000;
    const z = Math.min(vw / (width || 1), 1);
    setZoom(Math.max(0.25, z));
  };
  useEffect(() => { if (!loading && nodes.length) fit(); /* eslint-disable-next-line */ }, [loading, width]);

  // Snapshots the chart to a canvas at natural size and hands it back. Both the
  // PDF export and the Print sheet go through here, so the two outputs are
  // pixel-identical and the header/footer furniture only has to be laid out
  // once per medium rather than kept in sync by hand.
  // Snapshot the whole chart at natural size. Shared with the department chart
  // so both sheets are captured and corrected the same way — see lib/chartPdf.
  const renderChartCanvas = () => captureChart({
    content: contentRef.current,
    wrapper: contentRef.current?.parentElement,
    scrollView: viewRef.current,
    width,
    height,
    label: 'OrgChart',
  });

  const sheetTitle = () => {
    const unitLabel = (activeUnit && !isAllUnits) ? activeUnit : 'All Units';
    return `Org Chart — ${deptFilter ? deptFilter : unitLabel}`;
  };

  // Export the ENTIRE chart at natural size (not the visible viewport). The PDF
  // page is sized to the chart's aspect ratio so everything fits on one legible
  // page, with a fixed-height band above it for the title + controlled-document
  // box and a fixed-height band below it for the sign-off line.
  // Export the ENTIRE chart at natural size (not the visible viewport), onto a
  // page sized to the chart's aspect ratio so everything fits on one legible
  // sheet with the controlled-document header and the sign-off footer.
  const exportPdf = async () => {
    toast.message('Preparing PDF…', { description: 'Rendering full chart at high resolution' });
    try {
      await chartCanvasToPdf(await renderChartCanvas(), sheetTitle());
    } catch (e) {
      console.error('[OrgChart PDF] failed', e);
      toast.error('PDF export failed');
    }
  };

  // Print the chart through the same capture the PDF uses, on a self-contained
  // sheet: title + controlled-document box in the header, the chart scaled to
  // one landscape page, sign-off in the footer. The sheet is mounted into the
  // page (see PRINT_SHEET_CSS) rather than a pop-up window: a pop-up takes
  // focus, which backgrounds this tab and lets the browser throttle the
  // requestAnimationFrame the capture waits on, so the print never starts.
  const printChart = async () => {
    if (printing) return;
    setPrinting(true);
    toast.message('Preparing the chart\u2026', { description: 'Rendering full chart at high resolution' });
    try {
      const canvas = await renderChartCanvas();
      setPrintSheet({
        src: canvas.toDataURL('image/png'),
        title: sheetTitle(),
        date: new Date().toLocaleDateString(),
        landscape: canvas.width >= canvas.height,
      });
    } catch (e) {
      console.error('[OrgChart print] failed', e);
      toast.error('Could not prepare the chart for printing');
      setPrinting(false);
    }
  };

  // Hand off to the browser only once the sheet's image has actually decoded,
  // otherwise the print preview opens on an empty chart area.
  useEffect(() => {
    if (!printSheet) return undefined;
    let cancelled = false;
    (async () => {
      const img = printImgRef.current;
      if (img && !img.complete) {
        await new Promise((r) => { img.onload = r; img.onerror = r; });
      }
      if (cancelled) return;
      try { window.print(); } finally {
        if (!cancelled) { setPrintSheet(null); setPrinting(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [printSheet]);

  const exportExcel = () => {
    const rows = [['Name', 'Designation', 'Department', 'Unit', 'Level', 'Reports To']];
    positioned.forEach((n) => rows.push([n.name, n.designation, n.department, n.unit, n.level, byId[n.reporting_to]?.name || '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'org-chart.csv'; a.click(); URL.revokeObjectURL(url);
  };

  // Fresh idempotency key per modal open — accidental double-submits collapse
  // to one insert on the backend, but a deliberate second vacancy at the same
  // position (opened via a NEW modal) comes with a NEW key and inserts.
  const newIdem = () => (window.crypto?.randomUUID?.() || (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)));
  const openAddVacancy = () => setVacForm({ title: '', department: '', unit: isAllUnits ? '' : (activeUnit || ''), reporting_to: '', notes: '', idem: newIdem() });
  const openEditVacancy = (n) => setVacForm({ id: n.id, title: n.designation || '', department: n.department || '', unit: n.unit || '', reporting_to: n.reporting_to || '', notes: '', idem: newIdem() });
  const saveVacancy = async () => {
    if (savingVac) return;   // hard-block double-clicks even before disabled prop takes effect
    const unitVal = vacForm.unit || (isAllUnits ? '' : activeUnit);
    if (!unitVal) { toast.error('Pick a unit for this vacancy'); return; }
    setSavingVac(true);
    try {
      const payload = {
        title: vacForm.title, department: vacForm.department, unit: unitVal,
        reporting_to: vacForm.reporting_to || null, notes: vacForm.notes || '',
        idempotency_key: vacForm.idem,
      };
      if (vacForm.id) await vacancyApi.update(vacForm.id, payload);
      else await vacancyApi.create(payload);
      toast.success('Vacancy saved');
      setVacForm(null);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save vacancy');
    } finally {
      setSavingVac(false);
    }
  };
  const removeVacancy = async (id) => {
    if (!window.confirm('Remove this vacancy?')) return;
    try { await vacancyApi.remove(id); toast.success('Vacancy removed'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove vacancy'); }
  };

  const openJd = async (n) => {
    setJdVac(n); setJdText(''); setJdMethod(''); setJdLoading(true);
    try {
      const r = await vacancyApi.generateJd(n.id, false);
      setJdText(r.data.jd || ''); setJdMethod(r.data.method || '');
    } catch (e) { toast.error(e?.response?.data?.detail || 'JD generation failed'); }
    finally { setJdLoading(false); }
  };
  const regenJd = async () => {
    if (!jdVac) return; setJdLoading(true);
    try { const r = await vacancyApi.generateJd(jdVac.id, true); setJdText(r.data.jd || ''); setJdMethod(r.data.method || ''); }
    catch (e) { toast.error('Regeneration failed'); } finally { setJdLoading(false); }
  };
  const saveJd = async () => {
    if (!jdVac) return;
    try { await vacancyApi.update(jdVac.id, { jd: jdText }); toast.success('JD saved'); await load(); }
    catch (e) { toast.error('Could not save JD'); }
  };
  const downloadJd = () => {
    const blob = new Blob([jdText || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `JD-${(jdVac?.designation || 'post').replace(/[^a-z0-9]+/gi, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printJd = () => {
    const w = window.open('', '_blank'); if (!w) { toast.error('Allow pop-ups to print'); return; }
    const safe = (jdText || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>JD — ${jdVac?.designation || ''}</title>
      <style>@page{size:A4;margin:20mm}body{font-family:Georgia,serif;font-size:13px;line-height:1.6;color:#11182B;white-space:pre-wrap}
      h1{font-size:20px;border-bottom:2px solid #11182B;padding-bottom:6px}.btn{font-family:sans-serif;margin-bottom:14px}@media print{.btn{display:none}}</style></head>
      <body><div class="btn"><button onclick="window.print()">Print / Save as PDF</button></div>
      <h1>Job Description — ${jdVac?.designation || ''}</h1>${safe}</body></html>`);
    w.document.close();
  };

  // Mounted as a sibling of the app root so the print stylesheet can hide the
  // shell with a single `body > *:not(...)` rule.
  const printSheetPortal = printSheet && createPortal(
    <div data-org-print-sheet="" data-testid="org-print-sheet">
      <style>{PRINT_SHEET_CSS}</style>
      <style>{`@media print { @page { size: A4 ${printSheet.landscape ? 'landscape' : 'portrait'}; margin: 10mm; } }`}</style>
      <div className="org-sheet">
        <div className="org-sheet-head">
          <div>
            <h1>{printSheet.title}</h1>
            <div className="org-sheet-sub">Generated {printSheet.date}</div>
          </div>
          <table className="org-doc-box">
            <tbody>
              {DOC_CONTROL.map(([label, value]) => (
                <tr key={label}><th>{label}</th><td>{value}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="org-sheet-chart">
          <img ref={printImgRef} src={printSheet.src} alt={printSheet.title} />
        </div>
        <div className="org-sheet-foot">
          <span><b>Prepared By:</b> {SIGN_OFF.prepared}</span>
          <span><b>Approved By:</b> {SIGN_OFF.approved}</span>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <div data-testid="orgchart-page" className="flex h-screen app-shell">
      {printSheetPortal}
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 flex flex-col overflow-hidden p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Org Chart{deptFilter ? ` — ${deptFilter}` : ''}</h1>
            <div className="flex items-center gap-2">
              <select data-testid="org-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5" title="Show one department's chart (top management → department)">
                <option value="">Full org</option>
                {/* One row per group. A department that sits inside another
                    (QA, QC, SQA, Standard Room inside Quality) is drawn by
                    picking its parent, so listing it again only made the org
                    structure look like six separate Quality departments. */}
                {departmentGroups(masters.departments).map((d) => (
                  <option key={d.id} value={d.name}>{d.name}</option>
                ))}
              </select>
              <div className="w-px h-6 bg-[#E6E9F0] mx-1" />
              {canCreateVacancy && <Button data-testid="add-vacancy" variant="outline" onClick={openAddVacancy}><Plus className="w-4 h-4 mr-1" />Vacancy</Button>}
              <div className="w-px h-6 bg-[#E6E9F0] mx-1" />
              <Button data-testid="zoom-out" variant="outline" onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))}><ZoomOut className="w-4 h-4" /></Button>
              <Button data-testid="zoom-fit" variant="outline" onClick={fit}><Maximize2 className="w-4 h-4 mr-1" />Fit</Button>
              <Button data-testid="zoom-in" variant="outline" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}><ZoomIn className="w-4 h-4" /></Button>
              <div className="w-px h-6 bg-[#E6E9F0] mx-1" />
              <Button data-testid="org-print" variant="outline" onClick={printChart} disabled={printing}><Printer className="w-4 h-4 mr-1" />{printing ? 'Preparing…' : 'Print'}</Button>
              <Button data-testid="org-export-pdf" variant="outline" onClick={exportPdf}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
              <Button data-testid="org-export-excel" variant="outline" onClick={exportExcel}><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
            </div>
          </div>

          {/* The chart is a document about the management structure, so the
              junior grades are left off it. Said here rather than left for
              somebody to notice people are missing — they are not missing from
              the HRMS, only from this drawing. */}
          {levelView?.hidden?.length ? (
            <div data-testid="org-level-visibility-note"
              className="text-[11px] text-[#5A6377] bg-[#F7F8FB] border border-[#E6E9F0] rounded-[10px] px-3 py-2 mb-3">
              Showing <b>{levelRanges(levelView.visible)}</b>. {levelRanges(levelView.hidden)} are
              not drawn on the organisational chart — those employees remain in the HRMS and in
              Attendance, Late Coming, Leave, Payroll, Reports and the Employee Master. Change this
              in Settings → Organisational chart levels.
            </div>
          ) : null}

          {loading ? <div className="text-sm text-[#5A6377]">Loading…</div> : (
            <div ref={viewRef} className="flex-1 overflow-auto border border-[#E6E9F0] rounded-[14px] bg-white" id="orgchart-view">
              <div style={{ width: width * zoom, height: height * zoom }}>
                <div ref={contentRef} style={{ width, height, transform: `scale(${zoom})`, transformOrigin: 'top left', position: 'relative' }}>
                  <svg width={width} height={height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {/* A hairline per grade row, so the eye can follow a level
                        straight across a wide chart. */}
                    {levelRows.map((r) => (
                      <line key={`g${r.y}`} x1={RAIL_W - 8} x2={width} y1={r.y + CARD_H / 2} y2={r.y + CARD_H / 2}
                        stroke="#EEF1F6" strokeWidth="1" />
                    ))}
                    <defs>
                      <marker id="arrow" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                        <path d="M0,0 L6,3 L0,6 Z" fill="#102468" />
                      </marker>
                      <marker id="arrow-up" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                        <path d="M0,0 L6,3 L0,6 Z" fill="#B14A0E" />
                      </marker>
                    </defs>
                    {edges.map((e, i) => {
                      const x1 = e.from.x + CARD_W / 2, x2 = e.to.x + CARD_W / 2;
                      // Normally the line leaves the bottom of the manager's
                      // card and meets the top of their report's. Where the
                      // report outranks the manager it has to travel upwards,
                      // so it leaves the TOP of the manager's card and lands on
                      // the BOTTOM of theirs — otherwise it would be drawn
                      // straight through both cards.
                      const y1 = e.inverted ? e.from.y : e.from.y + CARD_H;
                      const y2 = e.inverted ? e.to.y + CARD_H : e.to.y;
                      const my = (y1 + y2) / 2;
                      return <path key={i} d={`M ${x1} ${y1} V ${my} H ${x2} V ${y2}`}
                        stroke={e.inverted ? '#E9A23B' : '#102468'} strokeWidth="1.75"
                        strokeDasharray={e.inverted ? '5 3' : undefined} fill="none"
                        markerEnd={e.inverted ? 'url(#arrow-up)' : 'url(#arrow)'}>
                        {e.inverted && <title>{`${e.to.name} (L${e.to.level}) outranks their manager ${e.from.name} (L${e.from.level})`}</title>}
                      </path>;
                    })}
                  </svg>
                  {/* The grade rail — drawn after the lines so a hairline
                      cannot cross a heading. Inside the captured area on
                      purpose, so the printed sheet and the PDF carry the same
                      headings the screen does. A row holds one grade, so the
                      chart reads as the ladder it stands on: the top grade
                      down to the lowest one the chart is configured to draw. */}
                  {levelRows.map((r) => {
                    const lc = levelColor(r.level);
                    const roles = designationsAt(r.level);
                    return (
                      <div key={r.y} data-testid={`org-level-row-${r.level ?? 'none'}`}
                        style={{ position: 'absolute', left: 0, top: r.y, width: RAIL_W, height: CARD_H,
                                 background: r.count ? lc.bg : '#FFFFFF',
                                 border: `1px ${r.count ? 'solid' : 'dashed'} ${lc.border}` }}
                        className="flex flex-col items-center justify-center rounded-[10px]"
                        title={r.level
                          ? `Level ${r.level}${roles ? ` — ${roles}` : ''} — ${r.count ? `${r.count} on this row` : 'vacant'}`
                          : `${r.count} with no level recorded`}>
                        <span className="text-[15px] font-bold leading-none" style={{ color: lc.text }}>
                          {r.level ? `L${r.level}` : '—'}
                        </span>
                        {/* A rung the structure has but nobody holds. Said on
                            the chart, because an unlabelled blank row reads as
                            a drawing fault rather than an open position. */}
                        <span className="text-[8px] mt-1 font-semibold tracking-wide"
                          style={{ color: r.count ? lc.text : '#B14A0E', opacity: r.count ? 0.7 : 1 }}>
                          {r.count || 'VACANT'}
                        </span>
                      </div>
                    );
                  })}
                  {positioned.map((n) => {
                    if (n.is_vacant) {
                      const lc = levelColor(n.level);
                      return (
                        <div key={n.id} data-testid={`org-vacancy-${n.id}`}
                          style={{ position: 'absolute', left: n.x, top: n.y, width: CARD_W, borderColor: lc.border }}
                          className="group relative rounded-[12px] p-3 text-center border-2 border-dashed bg-[repeating-linear-gradient(45deg,#FBFBFD,#FBFBFD_8px,#F2F4F8_8px,#F2F4F8_16px)]">
                          <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-1 bg-white border border-dashed" style={{ borderColor: lc.border }}>
                            <User className="w-5 h-5" strokeWidth={1.5} style={{ color: lc.text }} />
                          </div>
                          <div className="text-xs font-semibold leading-tight text-[#5A6377]">(Vacant)</div>
                          <div className="text-[10px]" style={{ color: lc.text }}>{n.designation}</div>
                          <div className="text-[9px] uppercase tracking-wide text-[#98A1B2]">{displayDepartment(n.department, masters.departments)}</div>
                          {n.level ? <span data-orgchart-level-pill="" className="inline-block text-[9px] px-1.5 rounded mt-1 font-semibold bg-white" style={{ color: lc.text }}>L{n.level}</span> : null}
                          {n.has_jd && <div className="text-[8px] text-[#15A36E] mt-1">JD ready</div>}
                          {/* Laid OVER the card, not inside it. Reserved in the
                              flow — even at opacity 0 — these three buttons made
                              a vacancy card 30px taller than an employee's, and
                              a row is a fixed height: the seat overlapped the
                              person reporting to it. */}
                          {canManage && (
                            <div className="absolute inset-x-0 bottom-1 opacity-0 group-hover:opacity-100 transition flex justify-center gap-1">
                              <button data-testid={`vac-jd-${n.id}`} title="Job description" onClick={() => openJd(n)} className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"><FileText className="w-3.5 h-3.5 text-[#2563EB]" /></button>
                              <button data-testid={`vac-edit-${n.id}`} title="Edit" onClick={() => openEditVacancy(n)} className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"><Pencil className="w-3.5 h-3.5 text-[#5A6377]" /></button>
                              <button data-testid={`vac-del-${n.id}`} title="Remove" onClick={() => removeVacancy(n.id)} className="p-1 rounded bg-white/90 hover:bg-white shadow-sm"><Trash2 className="w-3.5 h-3.5 text-[#E11D2A]" /></button>
                            </div>
                          )}
                        </div>
                      );
                    }
                    const navy = n.org_only || TOP_LEADER_IDS.includes(n.id);
                    const lc = levelColor(n.level);
                    return (
                      <div key={n.id} data-testid={`org-node-${n.id}`} onClick={() => { if (!n.org_only) navigate(`/employees/${n.id}`); }}
                        style={{ position: 'absolute', left: n.x, top: n.y, width: CARD_W,
                                 background: navy ? undefined : (n.on_notice ? '#FFFAF0' : lc.bg),
                                 borderColor: navy ? 'transparent' : (n.on_notice ? '#E9A23B' : lc.border) }}
                        className={`group relative rounded-[12px] p-3 shadow-sm border text-center ${n.org_only ? '' : 'cursor-pointer hover:shadow-md'} ${navy ? 'text-white' : ''} ${n.on_notice ? 'border-dashed' : ''}`}
                        title={n.org_only ? '' : 'Open profile'}>
                        {canManage && !n.org_only && (
                          <button
                            data-testid={`org-edit-${n.id}`}
                            title="Quick edit (designation / department / reports to)"
                            onClick={(e) => { e.stopPropagation(); openEditNode(n); }}
                            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition p-1 rounded bg-white/80 hover:bg-white shadow-sm"
                          >
                            <Pencil className="w-3.5 h-3.5 text-[#5A6377]" />
                          </button>
                        )}
                        <div style={navy ? { position: 'absolute', inset: 0, borderRadius: 12, background: 'linear-gradient(135deg,#0C1B57,#102468)', zIndex: -1 } : {}} />
                        {/* Serving notice. The chart keeps them in their seat
                            until their last working day and says so — reading
                            the SAME resignation record the Employees list and
                            the profile read. There is no org-chart status to
                            maintain separately. */}
                        {n.on_notice && (
                          <div data-testid={`org-notice-${n.id}`}
                               className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap
                                          text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full
                                          bg-[#FBEAD3] text-[#8A5A12] border border-[#F0D9AE] shadow-sm">
                            Resigned · LWD {n.last_working_day}
                          </div>
                        )}
                        <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-1 ${navy ? 'bg-white/15' : 'bg-white/70'}`}>
                          {n.photo_url ? <img src={resolveMediaUrl(n.photo_url)} alt={n.name} className="w-10 h-10 rounded-full object-cover" /> : <User className="w-5 h-5" strokeWidth={1.5} style={{ color: navy ? '#fff' : lc.text }} />}
                        </div>
                        <div className="text-xs font-semibold leading-tight" style={{ color: navy ? '#fff' : '#11182B' }}>{n.name}</div>
                        {/* Nobody is recorded above them, and they are not the
                            top of the organisation — so the chart says so
                            instead of drawing them as if they reported to no
                            one by design. */}
                        {n.noManager && (
                          <div data-testid={`org-no-manager-${n.id}`}
                               title="No reporting manager recorded — set one with the pencil"
                               className="mt-1 inline-block text-[8px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#FBF0E6] text-[#B14A0E]">
                            no manager
                          </div>
                        )}
                        <div className="text-[10px]" style={{ color: navy ? '#AFC0E8' : lc.text }}>
                          {n.designation}
                          {n.is_corporate && (
                            <span data-orgchart-corp-badge="" className="ml-1 align-middle text-[8px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#E8EFFD] text-[#15307F]">Corporate</span>
                          )}
                        </div>
                        <div className="text-[9px] uppercase tracking-wide" style={{ color: navy ? '#AFC0E8' : '#98A1B2' }}>{displayDepartment(n.department, masters.departments)}</div>
                        {n.level ? <span data-orgchart-level-pill="" className="inline-block text-[9px] px-1.5 rounded mt-1 font-semibold" style={{ background: navy ? 'rgba(255,255,255,0.15)' : '#FFFFFF', color: navy ? '#fff' : lc.text }}>L{n.level}</span> : null}
                        {canManage && n.org_only && (
                          <button data-testid={`org-convert-${n.id}`} onClick={(e) => { e.stopPropagation(); openConvert(n); }}
                            className="block mx-auto mt-2 text-[9px] px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white">
                            Make employee
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Quick edit an employee node — propagates everywhere */}
      <Dialog open={!!convertFor} onOpenChange={(o) => { if (!o) setConvertFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Make {convertFor?.name} an employee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5A6377]">Keeps this person&rsquo;s place in the org chart and adds them to the master &amp; salary sheet. Assign a home unit so they never orphan; add card, DOJ and salary from their profile afterwards.</p>
            <div>
              <label className="text-sm text-[#11182B] font-medium">Home unit</label>
              <select data-testid="convert-unit" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full mt-1"
                      value={convUnit} onChange={(e) => setConvUnit(e.target.value)}>
                {(masters.units || []).some((u) => u.name === 'Corporate Office') ? null : <option value="Corporate Office">Corporate Office</option>}
                {(masters.units || []).map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#11182B]">
              <input type="checkbox" data-testid="convert-corp" checked={convCorp} onChange={(e) => setConvCorp(e.target.checked)} />
              Show in every unit (corporate)
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConvertFor(null)}>Cancel</Button>
            <Button data-testid="convert-confirm" onClick={doConvert} disabled={convBusy || !convUnit}>{convBusy ? 'Working…' : 'Make employee'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editNode} onOpenChange={(o) => { if (!o) setEditNode(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit {editNode?.name}</DialogTitle></DialogHeader>
          {editNode && (
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Designation</Label>
                <select data-testid="orge-desig" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={editNode.designation} onChange={(e) => setEditNode({ ...editNode, designation: e.target.value })}>
                  <option value="">— select designation —</option>
                  {(masters.designations || []).map((d) => (
                    <option key={d.id} value={d.name}>{d.name}{d.level ? ` (L${d.level})` : ''}</option>
                  ))}
                </select></div>
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Department</Label>
                <select data-testid="orge-dept" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={editNode.department} onChange={(e) => setEditNode({ ...editNode, department: e.target.value })}>
                  <option value="">— select department —</option>
                  {departmentTree(masters.departments).map((d) => (
                    <option key={d.id} value={d.name}>{d.depth ? `   └ ${d.name}` : d.name}</option>
                  ))}
                </select></div>
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Reports to</Label>
                <select data-testid="orge-reports" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={editNode.reporting_to} onChange={(e) => setEditNode({ ...editNode, reporting_to: e.target.value })}>
                  <option value="">— None (top of tree) —</option>
                  {editMgrs.map((m) => (
                    <option key={m.id} value={m.id}>{m.level ? `L${m.level} · ` : ''}{m.name} — {m.designation}{m.is_corporate ? ' (Corporate)' : ''}</option>
                  ))}
                </select></div>
              <div className="text-xs text-[#98A1B2]">Unit: {editNode.unit} · Changes here update the employee everywhere (salary sheet, profile, lists).</div>
              <div className="flex justify-end pt-1">
                <Button data-testid="orge-save" onClick={saveNode} disabled={!editNode.designation || !editNode.department}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit vacancy */}
      <Dialog open={!!vacForm} onOpenChange={(o) => { if (!o && !savingVac) setVacForm(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{vacForm?.id ? 'Edit vacancy' : 'Add vacancy'}</DialogTitle></DialogHeader>
          {vacForm && (
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Title / Designation</Label>
                <select data-testid="vac-title" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={vacForm.title} onChange={(e) => setVacForm({ ...vacForm, title: e.target.value })}>
                  <option value="">— select designation —</option>
                  {(masters.designations || []).map((d) => (
                    <option key={d.id} value={d.name}>{d.name}{d.level ? ` (L${d.level})` : ''}</option>
                  ))}
                </select></div>
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Department</Label>
                <select data-testid="vac-dept" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={vacForm.department} onChange={(e) => setVacForm({ ...vacForm, department: e.target.value })}>
                  <option value="">— select department —</option>
                  {departmentTree(masters.departments).map((d) => (
                    <option key={d.id} value={d.name}>{d.depth ? `   └ ${d.name}` : d.name}</option>
                  ))}
                </select></div>
              {isAllUnits ? (
                <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Unit</Label>
                  <select data-testid="vac-unit" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                          value={vacForm.unit} onChange={(e) => setVacForm({ ...vacForm, unit: e.target.value })}>
                    <option value="">— select unit —</option>
                    {(masters.units || []).map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select></div>
              ) : (
                <div className="text-xs text-[#5A6377]">Unit: <span className="font-medium text-[#11182B]">{vacForm.unit || activeUnit}</span></div>
              )}
              <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Reports to</Label>
                <select data-testid="vac-reports" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                        value={vacForm.reporting_to} onChange={(e) => setVacForm({ ...vacForm, reporting_to: e.target.value })}>
                  <option value="">— None (top of tree) —</option>
                  {employeeNodes.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.designation}</option>)}
                </select></div>
              <div className="flex justify-end pt-1">
                <Button data-testid="vac-save" onClick={saveVacancy}
                        disabled={!vacForm.title || !vacForm.department || savingVac}>
                  {savingVac ? 'Saving…' : 'Save vacancy'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* JD dialog */}
      <Dialog open={!!jdVac} onOpenChange={(o) => { if (!o) setJdVac(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Job Description — {jdVac?.designation}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#5A6377]">{jdLoading ? 'Generating…' : (jdMethod ? `Source: ${jdMethod === 'ai' ? 'AI-drafted' : 'template'}` : '')}</div>
            <textarea data-testid="jd-text" value={jdText} onChange={(e) => setJdText(e.target.value)} disabled={jdLoading}
              className="w-full h-80 border border-[#E6E9F0] rounded-md p-3 text-sm font-mono" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={regenJd} disabled={jdLoading} data-testid="jd-regen">Regenerate (AI)</Button>
              <Button variant="outline" onClick={printJd} disabled={jdLoading || !jdText} data-testid="jd-print">Print</Button>
              <Button variant="outline" onClick={downloadJd} disabled={jdLoading || !jdText} data-testid="jd-download">Download</Button>
              <Button onClick={saveJd} disabled={jdLoading || !jdText} data-testid="jd-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
